import React from "react";
import useBranding from "../hooks/useBranding";

/**
 * White-label brand logo. Shows the admin-uploaded logo image when present,
 * otherwise the default mark + dynamic wordmark (name + accent + tagline).
 * variant: "dark" (for light backgrounds) | "light" (for dark backgrounds)
 */
const PhotonLogo = ({ variant = "dark", showTagline = true, className = "" }) => {
  const b = useBranding();
  const primary = b.primary_color || "#0D1B2A";
  const accent = b.accent_color || "#06B6D4";
  const accent2 = b.accent2_color || "#10B981";
  const nameColor = variant === "light" ? "#FFFFFF" : primary;
  const tagColor = variant === "light" ? accent2 : primary;

  if (b.logo) {
    return (
      <div className={`flex items-center select-none ${className}`} data-testid="brand-logo">
        <img src={b.logo} alt={b.company_name} className="h-11 w-auto max-w-[200px] object-contain shrink-0" />
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2.5 select-none ${className}`} data-testid="brand-logo">
      <svg width="44" height="44" viewBox="0 0 100 100" className="shrink-0">
        <defs>
          <linearGradient id="ppRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={primary} />
            <stop offset="60%" stopColor={primary} />
            <stop offset="100%" stopColor={accent} />
          </linearGradient>
          <linearGradient id="ppArc" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={accent2} />
            <stop offset="100%" stopColor={accent} />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="48" fill="url(#ppRing)" />
        <circle cx="50" cy="50" r="38" fill={primary} />
        <path d="M50 12 A38 38 0 0 1 50 88 Z" fill="url(#ppArc)" />
        <path d="M57 22 L33 55 L48 55 L43 78 L67 45 L52 45 Z" fill={primary} />
        <path d="M57 22 L45 38.5 L52 45 L52 45 L57 22 Z" fill={accent} opacity="0.9" />
        <path d="M43 78 L48 55 L41.5 55 L43 78 Z" fill={accent} opacity="0.9" />
      </svg>

      <div className="flex flex-col leading-none">
        <div className="flex items-baseline tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <span className="text-[22px] font-extrabold" style={{ color: nameColor, letterSpacing: "0.5px" }}>
            {b.name_primary || b.company_name}
          </span>
          {b.name_accent && (
            <span className="text-[22px] font-extrabold" style={{ color: accent, letterSpacing: "0.5px" }}>
              {b.name_accent}
            </span>
          )}
        </div>
        {showTagline && b.tagline && (
          <span className="text-[8.5px] font-bold mt-0.5" style={{ color: tagColor, letterSpacing: "1.5px" }}>
            {b.tagline}
          </span>
        )}
      </div>
    </div>
  );
};

export default PhotonLogo;
